/**
 * Clutch Reel — which moments of a fan's recording make the cut. Pure + unit-tested.
 *
 * A play's "swing" is how far it moved the model: |pHome(at the play) − pHome(the point before)|,
 * from the room's real probability history. The reel keeps the top N plays inside the recording
 * (by swing), cuts `preMs` before → `postMs` after each play's mark, and plays them in game order.
 * With no model in the room (the dev fixture driver), it ranks by what the play did to the
 * scoreboard instead, and says so (`basis: 'plays'`).
 */
import { formatClock, type GameEvent, type ProbabilityPoint } from '@clutch/core';
import type { Mark } from '../studio/marks';

export type ReelSegment = {
  seq: number; // the play the segment is built around
  seqs: number[]; // every selected play inside it (after merging)
  /** Each selected play in the cut with its mark and swing, in game order (one unless merged). */
  beats: { seq: number; markMs: number; swing: number }[];
  markMs: number; // when the headline play arrived in the recording
  startMs: number;
  endMs: number;
  swing: number; // |ΔpHome| (basis 'model') or play weight (basis 'plays')
};

export type ReelSelection = { basis: 'model' | 'plays'; segments: ReelSegment[] };

export type ReelOptions = {
  durationMs: number;
  count?: number;
  preMs?: number;
  postMs?: number;
  /** Plays that can headline a segment (shots, FTs, turnovers…); others never do. */
  plays?: readonly Pick<GameEvent, 'seq' | 'type' | 'made' | 'points' | 'homeScore' | 'awayScore' | 'period' | 'clockSec'>[];
};

export const REEL_PRE_MS = 2500;
export const REEL_POST_MS = 2000;
const HEADLINE_TYPES = new Set<GameEvent['type']>(['shot', 'freethrow', 'turnover', 'steal', 'block']);

/** |ΔpHome| at each probability point's seq (consecutive points' difference). */
export function swingsBySeq(probability: readonly Pick<ProbabilityPoint, 'seq' | 'pHome'>[]): Map<number, number> {
  const out = new Map<number, number>();
  for (let i = 1; i < probability.length; i++) {
    const a = probability[i - 1]!;
    const b = probability[i]!;
    if (!Number.isFinite(a.pHome) || !Number.isFinite(b.pHome)) continue;
    out.set(b.seq, Math.abs(b.pHome - a.pHome));
  }
  return out;
}

type PlayLite = NonNullable<ReelOptions['plays']>[number];

/** Model-free weight: points scored, a lead change or tie, a late miss. Used only without a model. */
export function playWeight(e: PlayLite, prev?: Pick<GameEvent, 'homeScore' | 'awayScore'>): number {
  let w = 0;
  if (e.made && e.points) w += e.points;
  if (prev) {
    const before = Math.sign(prev.homeScore - prev.awayScore);
    const after = Math.sign(e.homeScore - e.awayScore);
    if (before !== after) w += 2; // lead change or tying play
  }
  if ((e.type === 'shot' || e.type === 'freethrow') && !e.made && e.period >= 4 && e.clockSec <= 24) w += 1;
  if (e.type === 'turnover' || e.type === 'steal' || e.type === 'block') w += 0.5;
  return w;
}

export function selectReelSegments(
  probability: readonly Pick<ProbabilityPoint, 'seq' | 'pHome'>[],
  marks: readonly Mark[],
  opts: ReelOptions,
): ReelSelection {
  const count = opts.count ?? 3;
  const pre = opts.preMs ?? REEL_PRE_MS;
  const post = opts.postMs ?? REEL_POST_MS;
  const dur = Math.max(0, opts.durationMs);
  const playBySeq = new Map((opts.plays ?? []).map((p) => [p.seq, p]));
  const eligible = marks.filter((m) => m.offsetMs <= dur && (!opts.plays || HEADLINE_TYPES.has(playBySeq.get(m.seq)?.type as GameEvent['type'])));

  const swings = swingsBySeq(probability);
  let basis: ReelSelection['basis'] = 'model';
  let scored = eligible.map((m) => ({ m, w: swings.get(m.seq) ?? 0 })).filter((x) => x.w > 0);
  if (scored.length === 0 && opts.plays) {
    basis = 'plays';
    const sorted = [...(opts.plays ?? [])].sort((a, b) => a.seq - b.seq);
    const prevOf = new Map<number, PlayLite>();
    for (let i = 1; i < sorted.length; i++) prevOf.set(sorted[i]!.seq, sorted[i - 1]!);
    scored = eligible
      .map((m) => ({ m, w: playWeight(playBySeq.get(m.seq)!, prevOf.get(m.seq)) }))
      .filter((x) => x.w > 0);
  }
  // Biggest swing first; ties go to the later play (closer to the finish).
  scored.sort((a, b) => b.w - a.w || b.m.seq - a.m.seq);
  const top = scored.slice(0, count).sort((a, b) => a.m.seq - b.m.seq);

  const segments: ReelSegment[] = [];
  for (const { m, w } of top) {
    const startMs = Math.max(0, m.offsetMs - pre);
    const endMs = Math.min(dur, m.offsetMs + post);
    const last = segments[segments.length - 1];
    if (last && startMs <= last.endMs) {
      // Overlapping cuts become one continuous cut, headlined by the bigger swing.
      last.endMs = Math.max(last.endMs, endMs);
      last.seqs.push(m.seq);
      last.beats.push({ seq: m.seq, markMs: m.offsetMs, swing: w });
      if (w > last.swing) Object.assign(last, { seq: m.seq, markMs: m.offsetMs, swing: w });
      continue;
    }
    if (endMs > startMs) segments.push({ seq: m.seq, seqs: [m.seq], beats: [{ seq: m.seq, markMs: m.offsetMs, swing: w }], markMs: m.offsetMs, startMs, endMs, swing: w });
  }
  return { basis, segments };
}

export const reelDurationMs = (segs: readonly Pick<ReelSegment, 'startMs' | 'endMs'>[]) =>
  segs.reduce((t, s) => t + (s.endMs - s.startMs), 0);

/** The play on screen at `ms` into the recording: the latest beat whose mark has passed (else the first). */
export function beatAt(seg: Pick<ReelSegment, 'beats'>, ms: number): { seq: number; markMs: number; swing: number } | undefined {
  let cur = seg.beats[0];
  for (const b of seg.beats) if (b.markMs <= ms) cur = b;
  return cur;
}

/**
 * Side-by-side: the same plays from a friend's take, synced on the play marks. Keeps only the
 * segments the friend's take also covers; the friend's cut keeps the same lead-in to the mark.
 */
export function pairSegments(
  mine: readonly ReelSegment[],
  friendMarks: readonly Mark[],
  friendDurationMs: number,
): (ReelSegment & { friendStartMs: number })[] {
  const out: (ReelSegment & { friendStartMs: number })[] = [];
  for (const seg of mine) {
    const fm = friendMarks.find((m) => m.seq === seg.seq);
    if (!fm) continue;
    const lead = seg.markMs - seg.startMs;
    const len = seg.endMs - seg.startMs;
    const friendStartMs = Math.max(0, Math.min(fm.offsetMs - lead, friendDurationMs - len));
    out.push({ ...seg, friendStartMs: Math.max(0, friendStartMs) });
  }
  return out;
}

/** How a broadcast names a made 2: dunk, layup, hook, jumper — else just a bucket. */
function twoPointKind(description: string): string {
  if (/dunk/i.test(description)) return 'DUNK';
  if (/layup|finger roll/i.test(description)) return 'LAYUP';
  if (/hook/i.test(description)) return 'HOOK';
  if (/jump|fadeaway|pullup|pull-up|floater|step ?back/i.test(description)) return 'JUMPER';
  return 'BUCKET';
}

/** "BRUNSON THREE · 107–106 · 0:08.5" (away–home, the broadcast order). */
export function reelCaption(e: Pick<GameEvent, 'type' | 'made' | 'points' | 'playerName' | 'awayScore' | 'homeScore' | 'period' | 'clockSec' | 'description'>): string {
  const who = (e.playerName ?? '').toUpperCase() || 'PLAY';
  let what: string;
  if (e.type === 'shot') what = e.made ? `${who} ${e.points === 3 ? 'THREE' : twoPointKind(e.description)}` : `${who} MISSES`;
  else if (e.type === 'freethrow') what = e.made ? `${who} FT` : `${who} MISSES FT`;
  else if (e.type === 'turnover') what = `${who} TURNOVER`;
  else if (e.type === 'steal') what = `${who} STEAL`;
  else if (e.type === 'block') what = `${who} BLOCK`;
  else what = who;
  return `${what} · ${e.awayScore}–${e.homeScore} · ${broadcastClock(e.period, e.clockSec)}`;
}

/** Game clock the way a broadcast shows it under a minute: "0:08.5" (not "8.5"). */
export function broadcastClock(period: number, clockSec: number): string {
  const c = formatClock(period, clockSec).split(' ')[1] ?? '';
  if (c.includes(':')) return c;
  const [whole, frac] = c.split('.');
  return `0:${(whole ?? '0').padStart(2, '0')}${frac ? `.${frac}` : ''}`;
}
